//framework
import { ArraySchema, MapSchema } from "@colyseus/schema";

//constants
import { GAME_STATES } from "constants/constants";

/* ----------------------- Seed players (expanded to 8) ----------------------- */
const SEED_PLAYERS = [
    {
        name: "Player 1",
        id: "1",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-0.png",
        connected: true,
        primaryPlayer: true,
        mdmdData: {
            totalMoney: 1_000_000,
            placedMoney: [500_000, 250_000, 250_000, 0], // A,B,C,D
            lockedIn: false,
            waitingToJoin: false,
            cashColor: "RED",
            furthestRound: 7,
        },
    },
    {
        name: "Player 2",
        id: "2",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-1.png",
        connected: true,
        mdmdData: {
            totalMoney: 200_000,
            placedMoney: [100_000, 50_000, 50_000, 0],
            lockedIn: true,
            waitingToJoin: false,
            cashColor: "GREEN",
            furthestRound: 7,
        },
    },
    {
        name: "Player 3",
        id: "3",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-2.png",
        connected: true,
        mdmdData: {
            totalMoney: 0,
            placedMoney: [0, 0, 0, 0],
            lockedIn: false,
            waitingToJoin: false,
            cashColor: "BLUE",
            furthestRound: 5,
        },
    },
    {
        name: "Player 4",
        id: "4",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-3.png",
        connected: true,
        mdmdData: {
            totalMoney: 800_000,
            placedMoney: [800_000, 0, 0, 0],
            lockedIn: false,
            waitingToJoin: false,
            cashColor: "YELLOW",
            furthestRound: 7,
        },
    },
    {
        name: "Player 5",
        id: "5",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-4.png",
        connected: true,
        mdmdData: {
            totalMoney: 500_000,
            placedMoney: [250_000, 250_000, 0, 0],
            lockedIn: true,
            waitingToJoin: false,
            cashColor: "CYAN",
            furthestRound: 7,
        },
    },
    {
        name: "Player 6",
        id: "6",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-5.png",
        connected: true,
        mdmdData: {
            totalMoney: 350_000,
            placedMoney: [0, 150_000, 200_000, 0],
            lockedIn: true,
            waitingToJoin: false,
            cashColor: "PURPLE",
            furthestRound: 7,
        },
    },
    {
        name: "Player 7",
        id: "7",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-6.png",
        connected: true,
        mdmdData: {
            totalMoney: 0,
            placedMoney: [0, 0, 0, 0],
            lockedIn: false,
            waitingToJoin: false,
            cashColor: "ORANGE",
            furthestRound: 4,
        },
    },
    {
        name: "Player 8",
        id: "8",
        avatar: "https://gamecity.blob.core.windows.net/static-assets/avatars/avatar-7.png",
        connected: true,
        mdmdData: {
            totalMoney: 0,
            placedMoney: [0, 0, 0, 0],
            lockedIn: false,
            waitingToJoin: false,
            cashColor: "PINK",
            furthestRound: 2,
        },
    },
];

/** Make a deep-ish clone and coerce ArraySchema fields */
function cloneSeedPlayer(seed, idx) {
    const placed = new ArraySchema(...seed.mdmdData.placedMoney);
    return {
        ...seed,
        id: String(idx ?? seed.id),
        mdmdData: {
            ...seed.mdmdData,
            placedMoney: placed,
        },
    };
}

/* ----------------------------- Test room ------------------------------ */
const TEST_ROOM_STATE = {
    mdmdData: {
        gameState: GAME_STATES.LOADING,
        requiredPlayers: 1,
        //categorySelected: "Geography",
        question: "Which of these cities has the largest population?",
        questionVo: "https://play360.blob.core.windows.net/play360-resources-dev/vo/questions/test/test_1.mp3",
        answers: new ArraySchema("London", "Paris", "Madrid", "Berlin"),
        correctAnswer: "London",
        roundNumber: 1,
        playerChoosingCategory: "1",
        categories: new ArraySchema("Geography", "History"),
        timer: 45,
        mutedSfx: false,
        mutedMusic: false,
    },

    gameStarted: true,
    hideAnswer: true,

    players: new MapSchema(), // start empty; we'll add via helper below
};

/* ----------------------------- Helpers ------------------------------- */

/** Remove all players from the MapSchema (in-place). */
function testRemoveAllPlayers(state = TEST_ROOM_STATE) {
    if (state.players && typeof state.players.clear === "function") {
        state.players.clear();
    } else {
        state.players = new MapSchema();
    }
    return state;
}

/**
 * Add players up to 8.
 * - If passed a number, adds that many from SEED_PLAYERS.
 * - If passed an array of plain player objects, uses those (up to 8).
 * Coerces placedMoney to ArraySchema each time.
 */
function testAddPlayers(countOrList = 8, state = TEST_ROOM_STATE) {
    const MAX = 8;

    const list = Array.isArray(countOrList) ? countOrList.slice(0, MAX) : SEED_PLAYERS.slice(0, Math.min(countOrList, MAX));

    if (!(state.players instanceof MapSchema)) {
        state.players = new MapSchema();
    }

    list.forEach((seed, i) => {
        const idx = i + 1; // keys "1".."8"
        const player = cloneSeedPlayer(seed, idx);
        state.players.set(String(idx), player);
    });

    return state;
}

/** Convenience: wipe then add N from seed. */
function testResetPlayers(count = 8, state = TEST_ROOM_STATE) {
    testRemoveAllPlayers(state);
    testAddPlayers(count, state);
    return state;
}

/** Convenience: get a simple array of current players. */
function testPlayersArray(state = TEST_ROOM_STATE) {
    const p = state.players;
    if (!p) return [];
    if (typeof p.values === "function") return Array.from(p.values());
    return Array.isArray(p) ? p : Object.values(p);
}

/* -------------------------- Export --------------------------- */
export {
    TEST_ROOM_STATE,
    testRemoveAllPlayers,
    testAddPlayers,
    testResetPlayers,
    testPlayersArray,
    SEED_PLAYERS,
};
